import type { VoidComponent } from "solid-js";
import { createMemo, Show } from "solid-js";
import { useTranslation } from "../../../state/i18n";
import { useModalManager } from "../../../components/modal/ModalManager";
import InputModal from "../../../components/modal/InputModal";
import { MultiSelectWithSearchInput } from "../../../components/menu/MultiSelectWithSearch";
import UserService from "../../../services/UserService";
import { useCustomerState } from "../../../state/customer";
import { toSelectOption } from "../../../utils/avatarUtil";
import { array, nonEmpty, object, pipe } from "valibot";
import { userReference } from "../../../utils/validation/idReferences";
import GenericFailureModal from "../../GenericFailureModal";
import { differenceWith, isNil } from "lodash-es";
import type { Group } from "../../../state/group";
import type { User } from "../../../state/user";
import type { GroupUser } from "../../../state/groupUser";

type AddUsersToGroupModalProps = {
  group: Group;
};

export const addUsersToGroupSchema = () =>
  object({
    users: pipe(array(userReference()), nonEmpty()),
  });

export const AddUsersToGroupModal: VoidComponent<AddUsersToGroupModalProps> = (props) => {
  const { t } = useTranslation();
  const { replace } = useModalManager();
  const customer = useCustomerState();
  const users = UserService.getUsers(customer.customerId);
  const groupUsers = UserService.getGroupUsers(customer.customerId, props.group.id);

  const userData = createMemo(() =>
    !isNil(users.data) && !isNil(groupUsers.data)
      ? {
          users: users.data,
          groupUsers: groupUsers.data,
        }
      : undefined,
  );

  return (
    <InputModal
      variant="default"
      size="large"
      title={t(($) => $.ADD_USERS_TO_GROUP_MODAL_TITLE, { name: props.group.name })}
      titleIcon="userGroup"
      cancelLabel={t(($) => $.CANCEL_BUTTON)}
      confirmLabel={t(($) => $.ADD_USERS_TO_GROUP_MODAL_ACTIONS_CONFIRM)}
      onConfirm={(value) =>
        new Promise((resolve, reject) =>
          UserService.addUsersToGroup(customer.customerId, props.group.id).mutate(value, {
            onSuccess: resolve,
            onError: (err) => {
              replace({
                content: () => (
                  <GenericFailureModal title={t(($) => $.ADD_USERS_TO_GROUP_FAILURE_MODAL_TITLE)} error={err} />
                ),
              });
              reject(err);
            },
          }),
        )
      }
      initialValue={{ users: [] }}
      schema={addUsersToGroupSchema()}
    >
      {({ value, updateValue, error }) => (
        <Show when={userData()}>
          {(data) => {
            const selectableUsers = differenceWith<User, GroupUser>(data().users, data().groupUsers, (a, b) =>
              a.id.equals(b.id),
            );

            return (
              <MultiSelectWithSearchInput
                label={t(($) => $.ADD_USERS_TO_GROUP_MODAL_USERS)}
                placeholder={t(($) => $.ADD_USERS_TO_GROUP_MODAL_USERS_PLACEHOLDER)}
                value={value("users")}
                onSelect={(value) => updateValue("users", value)}
                width="matchTrigger"
                required
                options={{
                  users: selectableUsers.map((user) => {
                    const option = toSelectOption(user);
                    return { ...option, value: option.value.id };
                  }),
                }}
                errorMessage={error("users")}
              />
            );
          }}
        </Show>
      )}
    </InputModal>
  );
};
